import { useState, useEffect, useRef } from "react";
import {
  ScheduleResourceResponse,
  getFacilityResources,
} from "app/utils/request/scheduling/facilityResources";

const NO_RESOURCES: ScheduleResourceResponse[] = [];

export interface UseFacilityResourcesResult {
  resources: ScheduleResourceResponse[];
  loading: boolean;
}

/**
 * Fetches the resources of a facility while `enabled` is true.
 *
 * The hook sends one request for each facility. A later toggle of `enabled`
 * keeps the list and sends no second request. While `enabled` is false, the
 * hook sends no request and returns an empty list.
 */
export default function useFacilityResources(
  facilityId: string | undefined,
  enabled: boolean,
): UseFacilityResourcesResult {
  const [resources, setResources] = useState<ScheduleResourceResponse[]>([]);
  const [loading, setLoading] = useState(false);
  // The facility that owns the list above. A toggle does not change it, so
  // the effect below skips the fetch after the first reply.
  const loadedFacilityId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (loadedFacilityId.current === facilityId) {
      return;
    }

    // Another facility owns the calendar now. Drop the bays of the last one.
    loadedFacilityId.current = undefined;
    setResources((current) => (current.length === 0 ? current : []));

    if (!facilityId || !enabled) {
      setLoading(false);
      return;
    }

    loadedFacilityId.current = facilityId;
    setLoading(true);
    getFacilityResources(facilityId)
      .then((fetchedResources) => {
        // A late reply for the last facility must not replace the list.
        if (loadedFacilityId.current !== facilityId) {
          return;
        }
        setResources(fetchedResources);
        setLoading(false);
      })
      .catch((error) => {
        if (loadedFacilityId.current !== facilityId) {
          return;
        }
        console.error("Error fetching facility resources:", error);
        // The request failed. Clear the mark, so the next toggle retries.
        loadedFacilityId.current = undefined;
        setLoading(false);
      });
  }, [facilityId, enabled]);

  // The list stays cached for the next toggle, but a caller with the feature
  // off gets no bays.
  return { resources: enabled ? resources : NO_RESOURCES, loading };
}
